import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { ITelegramUser, IWebApp } from "../types/telegram";
import axios from "../utils/axios";
import { useLocation } from "react-router-dom";
import { useLoading } from "./LoadingContext";
import { extractAuthToken, getTelegramInitData, getTelegramUserId, normalizeAuthHeader, setStoredAuthToken } from "../utils/auth";
import { AxiosResponse } from "axios";
import { palette } from "../theme/palette";

const atLeast = (app: IWebApp, version: string) =>
    typeof app.isVersionAtLeast === 'function' ? app.isVersionAtLeast(version) : false;

/**
 * Forces Telegram's own chrome to match the app background, so there is no
 * seam between the client header and the page. The app is always dark, so
 * colorScheme/themeParams are deliberately not read.
 *
 * Every call is guarded: older clients either lack the method entirely or
 * throw WebAppMethodUnsupported.
 */
const applyTelegramChrome = (app: IWebApp) => {
    try {
        app.ready();

        if (typeof app.expand === 'function' && !app.isExpanded) {
            app.expand();
        }

        if (typeof app.setBackgroundColor === 'function' && atLeast(app, '6.1')) {
            app.setBackgroundColor(palette.bg);
        }

        if (typeof app.setHeaderColor === 'function') {
            // An arbitrary hex needs 6.9; 6.1-6.8 only accept a colour key.
            if (atLeast(app, '6.9')) {
                app.setHeaderColor(palette.bg);
            } else if (atLeast(app, '6.1')) {
                app.setHeaderColor('bg_color');
            }
        }

        // Swipe-to-close otherwise fights the page scroll.
        if (typeof app.disableVerticalSwipes === 'function' && atLeast(app, '7.7')) {
            app.disableVerticalSwipes();
        }
    } catch (err) {
        console.warn('Telegram chrome setup skipped', err);
    }
};

export interface ITelegramContext {
    webApp?: IWebApp;
    user?: ITelegramUser;
    isAuthorizing: boolean;
    isAuthorized: boolean;
    authError: string;
}

export const TelegramContext = createContext<ITelegramContext>({
    isAuthorizing: true,
    isAuthorized: false,
    authError: '',
});

export const TelegramProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const location = useLocation()
    const { setIsFullScreenLoading } = useLoading()
    const [webApp, setWebApp] = useState<IWebApp | null>(null);
    const [isAuthorizing, setIsAuthorizing] = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authError, setAuthError] = useState('');
    
    const authorizeUser = useCallback(async (userId: string) => {
        setIsFullScreenLoading(true)
        setIsAuthorizing(true)
        setAuthError('')
        try {
            const response: AxiosResponse = await axios.post('/auth/auth/', { user_id: userId, init_data: getTelegramInitData() })
            const token = extractAuthToken(response);

            if (token) {
                setStoredAuthToken(token);
                axios.defaults.headers.common.Authorization = normalizeAuthHeader(token);
            }

            setIsAuthorized(true);
            return response;
        } catch (error: any) {
            setAuthError(error?.body?.message || 'Avtorizatsiya bajarilmadi');
            setIsAuthorized(false);
            throw error;
        } finally {
            setIsFullScreenLoading(false)
            setIsAuthorizing(false)
        }
    }, [setIsFullScreenLoading])

    useEffect(() => {
        const botId = location.pathname.split('/')[1] ?? ''

        if (botId) {
            axios.defaults.headers.common['X-Bot-Id'] = botId
        }
    }, [location.pathname])

    useEffect(() => {
        const app = (window as any).Telegram?.WebApp as IWebApp | undefined;
        const userId = getTelegramUserId();

        if (app) {
            applyTelegramChrome(app);
            setWebApp(app);
        }

        authorizeUser(userId).catch((err) => { console.log(err) })
    }, [authorizeUser]);

    const value = useMemo(() => {
        return webApp
            ? {
                webApp,
                unsafeData: webApp.initDataUnsafe,
                user: webApp.initDataUnsafe.user,
                isAuthorizing,
                isAuthorized,
                authError,
            }
            : {
                isAuthorizing,
                isAuthorized,
                authError,
            };
    }, [webApp, isAuthorizing, isAuthorized, authError]);

    return (
        <TelegramContext.Provider value={value}>
            {children}
        </TelegramContext.Provider>
    );
};

export const useTelegram = () => useContext(TelegramContext);